type TAmount = string | number | null | (string | number)[];

/** Monetary amounts are shown with two decimals (minor currency units) when they have a fraction. */
const FRACTION_DIGITS = 2;

const userLocale = navigator.language || 'en-US';

const parseAmount = (value: TAmount): number | null => {
  if (value === null || String(value).trim() === '') return null;

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

/**
 * Rounds to two decimals and shows either exactly two decimals (fractional amount)
 * or none (whole amount). Never rounds to an integer unless the fraction is below 0.005.
 */
const formatDecimal = (
  value: number,
  locale: string,
  useGrouping: boolean,
  fractionDigits = FRACTION_DIGITS,
): string => {
  const options: Intl.NumberFormatOptions = { useGrouping, maximumFractionDigits: fractionDigits };
  const hasFraction = new Intl.NumberFormat(locale, options)
    .formatToParts(value)
    .some(part => part.type === 'fraction');

  return value.toLocaleString(locale, {
    ...options,
    minimumFractionDigits: hasFraction ? fractionDigits : 0,
  });
};

/** Human-readable amount for tables and cards: locale thousand separators, decimals kept. */
export const formatAmount = (amount: TAmount): string | null => {
  const numericValue = parseAmount(amount);

  return numericValue === null ? null : formatDecimal(numericValue, userLocale, true);
};

/**
 * Whole-number amount for aggregate views (dashboard totals, analytics volumes, settlements):
 * rounded half-up to the nearest unit, locale thousand separators.
 */
export const formatWholeAmount = (amount: TAmount): string | null => {
  const numericValue = parseAmount(amount);

  return numericValue === null ? null : formatDecimal(numericValue, userLocale, true, 0);
};

/**
 * Plain amount for form inputs that are sent back to the API: no grouping and a dot
 * as the decimal separator regardless of the browser locale.
 */
export const toPlainAmount = (amount: TAmount): string => {
  const numericValue = parseAmount(amount);

  return numericValue === null ? '' : formatDecimal(numericValue, 'en-US', false);
};
